import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import { Input } from '../../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import {
  Newspaper, ExternalLink, Search, Loader2, RefreshCw,
  MessageCircle, TrendingUp, TrendingDown, Zap, Bell, FileText,
  Heart, Repeat2, Shield,
} from 'lucide-react';
import { usePortfolioStore } from '../../../state/portfolioStore';
import SectionNav from '../../../components/layout/SectionNav';
import PageHeader from '../../../components/layout/PageHeader';
import { portfolioTabs } from '../../../nav/publicNav';

const TYPE_ICONS = {
  news: Newspaper,
  x_post: MessageCircle,
  insider: TrendingUp,
  corporate_action: Zap,
  notice: FileText,
};

const TYPE_COLORS = {
  news: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
  x_post: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400',
  insider: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
  corporate_action: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400',
  notice: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-400',
};

const TYPE_LABELS = {
  news: 'News',
  x_post: 'X/Social',
  insider: 'Insider',
  corporate_action: 'Corp. Action',
  notice: 'BSE Notice',
};

function FeedCard({ item }) {
  const Icon = TYPE_ICONS[item.type] || Newspaper;
  const isXPost = item.type === 'x_post';
  const isInsider = item.type === 'insider';
  const isSell = isInsider && item.metadata?.type === 'sell';

  return (
    <div className="flex gap-3 p-4 border-b border-border/20 last:border-0 hover:bg-secondary/20 transition-colors">
      <div className="mt-0.5 shrink-0">
        {isInsider ? (
          isSell ? <TrendingDown className="w-4 h-4 text-red-500" /> : <TrendingUp className="w-4 h-4 text-emerald-500" />
        ) : (
          <Icon className="w-4 h-4 text-muted-foreground" />
        )}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1 flex-wrap">
          <Badge variant="secondary" className="text-[10px] px-1.5 py-0 h-4">{item.symbol}</Badge>
          <span className={`text-[9px] px-1.5 py-0.5 rounded-full ${TYPE_COLORS[item.type] || TYPE_COLORS.notice}`}>
            {TYPE_LABELS[item.type] || item.type}
          </span>
          {item.severity === 'high' && (
            <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400">Important</span>
          )}
        </div>

        {isXPost ? (
          <>
            <p className="text-xs font-medium text-foreground mb-0.5">@{item.metadata?.tier ? `${item.source}` : item.source}</p>
            <p className="text-xs text-muted-foreground line-clamp-3">{item.body}</p>
            <div className="flex items-center gap-3 mt-1.5">
              {item.metadata?.likes > 0 && (
                <span className="flex items-center gap-1 text-[10px] text-muted-foreground">
                  <Heart className="w-3 h-3" /> {item.metadata.likes}
                </span>
              )}
              {item.metadata?.reposts > 0 && (
                <span className="flex items-center gap-1 text-[10px] text-muted-foreground">
                  <Repeat2 className="w-3 h-3" /> {item.metadata.reposts}
                </span>
              )}
              {item.metadata?.credibility != null && (
                <span className="flex items-center gap-1 text-[10px] text-muted-foreground">
                  <Shield className="w-3 h-3" /> {(item.metadata.credibility * 100).toFixed(0)}%
                </span>
              )}
              {item.metadata?.tier && (
                <Badge variant="secondary" className="text-[9px] h-3.5 px-1">Tier {item.metadata.tier}</Badge>
              )}
            </div>
          </>
        ) : (
          <>
            <p className="text-sm font-medium text-foreground line-clamp-2 mb-0.5">{item.title}</p>
            {item.body && item.body !== item.title && (
              <p className="text-xs text-muted-foreground line-clamp-2">{item.body}</p>
            )}
          </>
        )}

        <div className="flex items-center gap-2 mt-1.5">
          {item.source && !isXPost && <span className="text-[10px] text-muted-foreground">{item.source}</span>}
          {item.date && (
            <span className="text-[10px] text-muted-foreground">
              {new Date(item.date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}
            </span>
          )}
          {item.url && (
            <a href={item.url} target="_blank" rel="noopener noreferrer" className="text-[10px] text-emerald-600 hover:underline flex items-center gap-0.5">
              Open <ExternalLink className="w-2.5 h-2.5" />
            </a>
          )}
        </div>
      </div>
    </div>
  );
}

function XSignalCard({ signal }) {
  return (
    <div className="p-4 border-b border-border/20 last:border-0 hover:bg-secondary/20 transition-colors">
      <div className="flex items-start gap-3">
        <div className="w-8 h-8 rounded-full bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center text-xs font-bold text-purple-600 dark:text-purple-400 shrink-0">
          {signal.handle?.[0]?.toUpperCase() || 'X'}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-0.5">
            <span className="text-sm font-medium">{signal.author || signal.handle}</span>
            <span className="text-[10px] text-muted-foreground">@{signal.handle}</span>
            {signal.tier && <Badge variant="secondary" className="text-[9px] h-3.5 px-1">Tier {signal.tier}</Badge>}
            <Badge variant="secondary" className="text-[10px] px-1.5 py-0 h-4">{signal.symbol}</Badge>
          </div>
          <p className="text-xs text-muted-foreground leading-relaxed">{signal.text}</p>
          <div className="flex items-center gap-4 mt-2">
            <span className="flex items-center gap-1 text-[10px] text-muted-foreground"><Heart className="w-3 h-3" /> {signal.likes}</span>
            <span className="flex items-center gap-1 text-[10px] text-muted-foreground"><Repeat2 className="w-3 h-3" /> {signal.reposts}</span>
            {signal.credibility != null && (
              <span className="flex items-center gap-1 text-[10px] text-muted-foreground"><Shield className="w-3 h-3" /> Cred: {(signal.credibility * 100).toFixed(0)}%</span>
            )}
            {signal.date && (
              <span className="text-[10px] text-muted-foreground">
                {new Date(signal.date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
              </span>
            )}
          </div>
          {signal.tags?.length > 0 && (
            <div className="flex flex-wrap gap-1 mt-1.5">
              {signal.tags.map((t) => <span key={t} className="text-[9px] px-1.5 py-0.5 rounded bg-secondary text-muted-foreground">{t}</span>)}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

const FILTER_TYPES = [
  { value: 'all', label: 'All' },
  { value: 'news', label: 'News' },
  { value: 'x_post', label: 'X/Social' },
  { value: 'insider', label: 'Insider' },
  { value: 'corporate_action', label: 'Corp. Actions' },
  { value: 'notice', label: 'BSE Notices' },
];

export default function NewsInsightsPage() {
  const holdings = usePortfolioStore((s) => s.holdings);
  const fetchHoldings = usePortfolioStore((s) => s.fetchHoldings);
  const unifiedFeed = usePortfolioStore((s) => s.unifiedFeed);
  const feedLoading = usePortfolioStore((s) => s.feedLoading);
  const fetchUnifiedFeed = usePortfolioStore((s) => s.fetchUnifiedFeed);
  const xSignals = usePortfolioStore((s) => s.xSignals);
  const xSignalsLoading = usePortfolioStore((s) => s.xSignalsLoading);
  const fetchXSignals = usePortfolioStore((s) => s.fetchXSignals);

  const [tab, setTab] = useState('feed');
  const [typeFilter, setTypeFilter] = useState('all');
  const [symbolFilter, setSymbolFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    if (!holdings.length) fetchHoldings();
  }, [fetchHoldings, holdings.length]);

  useEffect(() => {
    fetchUnifiedFeed('all', 30);
    fetchXSignals(7);
  }, [holdings.length, fetchUnifiedFeed, fetchXSignals]);

  const handleRefresh = async () => {
    setRefreshing(true);
    await Promise.all([fetchUnifiedFeed('all', 30), fetchXSignals(7)]);
    setRefreshing(false);
  };

  const symbols = [...new Set([
    ...holdings.map((h) => h.symbol),
    ...unifiedFeed.map((f) => f.symbol),
    ...xSignals.map((x) => x.symbol),
  ].filter(Boolean))].sort();

  const filteredFeed = unifiedFeed.filter((item) => {
    if (typeFilter !== 'all' && item.type !== typeFilter) return false;
    if (symbolFilter !== 'all' && item.symbol !== symbolFilter) return false;
    if (search) {
      const q = search.toLowerCase();
      return item.title?.toLowerCase().includes(q) || item.body?.toLowerCase().includes(q) || item.symbol?.toLowerCase().includes(q);
    }
    return true;
  });

  const filteredSignals = xSignals.filter((s) => {
    if (symbolFilter !== 'all' && s.symbol !== symbolFilter) return false;
    if (search) {
      const q = search.toLowerCase();
      return s.text?.toLowerCase().includes(q) || s.symbol?.toLowerCase().includes(q) || s.handle?.toLowerCase().includes(q);
    }
    return true;
  });

  return (
    <div className="space-y-4">
      <SectionNav tabs={portfolioTabs} />
      <PageHeader
        title="News & Insights"
        description="Everything happening with your portfolio"
        actions={
          <Button size="sm" variant="outline" className="text-xs gap-1.5 h-8" onClick={handleRefresh} disabled={refreshing}>
            {refreshing ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />} Refresh
          </Button>
        }
      />

      {/* Tab bar + filters */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
        <div className="flex gap-1 bg-secondary/50 rounded-lg p-0.5">
          <button className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${tab === 'feed' ? 'bg-background shadow-sm' : 'text-muted-foreground hover:text-foreground'}`} onClick={() => setTab('feed')}>
            <Newspaper className="w-3.5 h-3.5 inline mr-1" /> Feed ({unifiedFeed.length})
          </button>
          <button className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${tab === 'x' ? 'bg-background shadow-sm' : 'text-muted-foreground hover:text-foreground'}`} onClick={() => setTab('x')}>
            <MessageCircle className="w-3.5 h-3.5 inline mr-1" /> X/Social ({xSignals.length})
          </button>
        </div>

        <div className="flex flex-wrap gap-2 flex-1">
          {tab === 'feed' && (
            <div className="flex gap-1 flex-wrap">
              {FILTER_TYPES.map((ft) => (
                <button
                  key={ft.value}
                  className={`px-2.5 py-1 text-[11px] rounded-full border transition-colors ${typeFilter === ft.value ? 'bg-emerald-600 text-white border-emerald-600' : 'border-border text-muted-foreground hover:border-foreground'}`}
                  onClick={() => setTypeFilter(ft.value)}
                >
                  {ft.label}
                </button>
              ))}
            </div>
          )}

          <Select value={symbolFilter} onValueChange={setSymbolFilter}>
            <SelectTrigger className="w-[140px] h-7 text-xs"><SelectValue placeholder="Stock" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Stocks</SelectItem>
              {symbols.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>

          <div className="relative max-w-[200px]">
            <Search className="w-3 h-3 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input className="pl-8 h-7 text-xs" placeholder="Search..." value={search} onChange={(e) => setSearch(e.target.value)} />
          </div>
        </div>
      </div>

      {/* Content */}
      {tab === 'feed' ? (
        <Card className="border-border/40">
          <CardContent className="p-0">
            {feedLoading && unifiedFeed.length === 0 ? (
              <div className="flex items-center justify-center py-16 gap-2">
                <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
                <span className="text-sm text-muted-foreground">Loading portfolio feed...</span>
              </div>
            ) : filteredFeed.length > 0 ? (
              <div className="max-h-[700px] overflow-y-auto">
                {filteredFeed.map((item, i) => <FeedCard key={item.id || i} item={item} />)}
              </div>
            ) : (
              <div className="py-16 text-center">
                <Newspaper className="w-8 h-8 mx-auto text-muted-foreground mb-2" />
                <p className="text-sm text-muted-foreground">No items match your filters</p>
              </div>
            )}
          </CardContent>
        </Card>
      ) : (
        <Card className="border-border/40">
          <CardContent className="p-0">
            {xSignalsLoading && xSignals.length === 0 ? (
              <div className="flex items-center justify-center py-16 gap-2">
                <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
                <span className="text-sm text-muted-foreground">Loading X signals...</span>
              </div>
            ) : filteredSignals.length > 0 ? (
              <div className="max-h-[700px] overflow-y-auto">
                {filteredSignals.map((signal, i) => <XSignalCard key={signal.id || i} signal={signal} />)}
              </div>
            ) : (
              <div className="py-16 text-center">
                <MessageCircle className="w-8 h-8 mx-auto text-muted-foreground mb-2" />
                <p className="text-sm text-muted-foreground">No X/Twitter signals found for your holdings</p>
                <p className="text-xs text-muted-foreground mt-1">Signals are populated from ingested X data</p>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
